import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BatchCloneHorsesData, CloneHorseData, CreateHorseData, Horse } from '@sleipai/sleip-models';
import * as HorseActions from '../actions/horses.actions';
import * as HorseReducers from '../reducers/horses.reducer';
import * as HorseSelctors from '../selectors/horses.selectors';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { createChunksOfTen } from '../../helpers';
import { QueryConfig } from '../../helpers/query-config';
import { ClientWebAppProjectType } from '../../env-configuration';

@Injectable()
export class HorseStoreFacade {
  constructor(private readonly store: Store<HorseReducers.State>) {}

  getHorseEntityById$ = (horseId?: string) =>
    this.store.pipe(select(HorseSelctors.selectSingleHorse(horseId)));

  isHorseEntityLoaded$ = (horseId: string) =>
    this.store.pipe(
      select(HorseSelctors.selectSingleHorse(horseId)),
      map((horse: Horse | null) => horse !== null)
    );

  //Horses owned by the user
  getHorseEntityListForOwner$ = (ownerUserId: string) =>
    this.store.pipe(select(HorseSelctors.selectHorseWithOwner(ownerUserId)));

  isHorseEntityListForOwnerLoaded$ = (ownerUserId: string) =>
    this.store.pipe(
      select(HorseSelctors.selectHorseWithOwner(ownerUserId)),
      map((horses: Horse[] | null) => horses !== null && horses.length !== 0)
    );

  //Horses associated with the user
  getHorseEntityListAssociatedWithUser$ = (userId: string) =>
    this.store.pipe(
      select(HorseSelctors.selectHorsesAssociatedWithUser(userId))
    );

  isHorseEntityListAssociatedWithUserLoaded$ = (userId: string) =>
    this.store.pipe(
      select(HorseSelctors.selectHorsesAssociatedWithUser(userId)),
      map((horses: Horse[] | null) => horses !== null && horses.length !== 0)
    );

  //Horses by horse Ids
  fetchHorseEntityListForHorseIds$ = (horseIds: string[]) => {
    const chunks = createChunksOfTen(horseIds);
    chunks.map((uids: any) => {
      this.store.dispatch(HorseActions.loadHorsesByIds({ horseIds: uids }));
    });
  };

  getHorseEntityListForHorseIds = (horseIds: string[]) =>
    this.store.pipe(select(HorseSelctors.selectHorsesWithHorseIds(horseIds)));

  isHorseEntityListForHorseIdsLoaded$ = (horseIds: string[]) =>
    this.store.pipe(
      select(HorseSelctors.selectHorsesWithHorseIds(horseIds)),
      map(
        (horses: Horse[] | null) =>
          horses !== null && horses.length === [...new Set(horseIds)].length
      ),
      map((loaded) => {
        if (!loaded)
          this.store.dispatch(
            HorseActions.loadHorsesByIds({ horseIds: horseIds })
          );
        return loaded;
      })
    );

  getTotalHorseCount$: Observable<number> = this.store.pipe(
    select(HorseSelctors.selectTotalHorseCount)
  );

  getNumberOfPages$: Observable<number> = this.store.pipe(
    select(HorseSelctors.selectNumberOfPages)
  );

  fetchHorsesListNextPage$ = (
    queryConfig: QueryConfig,
    updateTotalCount: boolean = false
  ) => {
    this.store.dispatch(
      HorseActions.loadHorsesFromAlgoliaSidb({ queryConfig, updateTotalCount })
    );
  };

  getHorsesListPage$ = (
    queryConfig: QueryConfig,
    clientWebAppProject?: ClientWebAppProjectType
  ) => {
    return this.store.pipe(
      select(
        HorseSelctors.selectHorsesListPage(queryConfig, clientWebAppProject)
      )
    );
  };

  getIsLoading$: Observable<boolean> = this.store.pipe(
    select(HorseSelctors.selectIsLoading)
  );

  createHorseWithTags$ = (horseToCreate: CreateHorseData) =>
    this.store.dispatch(
      HorseActions.createHorseWithTags({
        data: horseToCreate,
      })
    );

  updateHorseWithTags$ = (horseToUpdate: Horse) =>
    this.store.dispatch(
      HorseActions.updateHorseWithTags({ data: horseToUpdate })
    );

  getHorseActionSuccessType$ = this.store.pipe(
    select(HorseSelctors.selectHorseActionSuccessType)
  );

  getHorseSaved$: Observable<boolean> = this.store.pipe(
    select(HorseSelctors.selectHorseSaved)
  );

  getHorseError$: Observable<string | null> = this.store.pipe(
    select(HorseSelctors.selectHorseError)
  );

  removeHorse$ = (horseToDelete: Horse) =>
    this.store.dispatch(HorseActions.removeHorse({ horseToDelete }));

  updateHorseName$ = (horseToUpdate: Horse, name: string) =>
    this.store.dispatch(HorseActions.updateHorseName({ horseToUpdate, name }));

  cloneHorse$ = (cloneHorse: CloneHorseData) =>
    this.store.dispatch(HorseActions.cloneHorse({ cloneHorse }));

  getHorseRemoved$: Observable<boolean> = this.store.pipe(
    select(HorseSelctors.selectHorseRemoved)
  );

  batchStopShareHorse$ = (uids: string[], horseId: string) =>
    this.store.dispatch(HorseActions.batchStopShareHorse({ uids, horseId }));

  getBatchStopShareFailedIds$: Observable<string[] | null> = this.store.pipe(
    select(HorseSelctors.selectBatchStopShareFailedIds)
  );

  mergeHorses$ = (horseIdToMerge: string, horseIdToMergeTo: string) =>
    this.store.dispatch(
      HorseActions.mergeHorses({ horseIdToMerge, horseIdToMergeTo })
    );

  batchDeleteHorses$ = (ids: string[]) =>
    this.store.dispatch(HorseActions.batchDeleteHorses({ ids }));

  getBatchDeleteFailedIds$: Observable<string[] | null> = this.store.pipe(
    select(HorseSelctors.selectBatchDeleteFailedIds)
  );

  batchCloneHorses$ = (data: BatchCloneHorsesData) =>
    this.store.dispatch(HorseActions.batchCloneHorses({ data }));

  getBatchCloneFailedIds$: Observable<string[] | null> = this.store.pipe(
    select(HorseSelctors.selectBatchCloneFailedIds)
  );

  fetchHorsesForInfiniteScrollList$ = (
    queryConfig: QueryConfig,
    reset: boolean
  ) => {
    this.store.dispatch(
      HorseActions.loadHorsesForInfiniteScrollList({ queryConfig, reset })
    );
  };

  getHasMoreHorses$: Observable<boolean> = this.store.pipe(
    select(HorseSelctors.selectHorsesHasMore)
  );
}
